import React, { useEffect, useState } from "react";
import api, { extractApiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * Iteration 90 — checkpoint: admin-automatic-discounts-with-session-targets.
 *
 * Admin panel for the two independent automatic (no-code) discount
 * campaigns: BOOK and SESSIONS. Persisted server-side in
 * `db.system_settings` under key `automatic_discounts` and consumed by
 * `/coupons/validate` and `/checkout/create` — the frontend just previews.
 */
const AutoDiscountsPanel = () => {
  const [book, setBook] = useState({
    enabled: false,
    percentage: "",
    start_date: "",
    end_date: "",
  });
  const [sessions, setSessions] = useState({
    enabled: false,
    percentage: "",
    start_date: "",
    end_date: "",
    target_mode: "all_sessions",
    selected_session_ids: [],
  });
  const [choices, setChoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const applyPayload = (data) => {
    const s = data.settings || {};
    setBook({
      enabled: !!s.book?.enabled,
      percentage: s.book?.percentage ?? "",
      start_date: s.book?.start_date || "",
      end_date: s.book?.end_date || "",
    });
    setSessions({
      enabled: !!s.sessions?.enabled,
      percentage: s.sessions?.percentage ?? "",
      start_date: s.sessions?.start_date || "",
      end_date: s.sessions?.end_date || "",
      target_mode: s.sessions?.target_mode || "all_sessions",
      selected_session_ids: [...(s.sessions?.selected_session_ids || [])],
    });
    setChoices(data.session_choices || []);
  };

  useEffect(() => {
    api
      .get("/admin/automatic-discounts")
      .then(({ data }) => applyPayload(data))
      .catch((e) => toast.error(extractApiErrorMessage(e, "تعذّر تحميل العروض التلقائية")))
      .finally(() => setLoading(false));
  }, []);

  const toggleChoice = (id) => {
    setSessions((s) => {
      const has = s.selected_session_ids.includes(id);
      return {
        ...s,
        selected_session_ids: has
          ? s.selected_session_ids.filter((x) => x !== id)
          : [...s.selected_session_ids, id],
      };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        book: {
          enabled: !!book.enabled,
          percentage: Number(book.percentage) || 0,
          start_date: book.start_date || null,
          end_date: book.end_date || null,
        },
        sessions: {
          enabled: !!sessions.enabled,
          percentage: Number(sessions.percentage) || 0,
          start_date: sessions.start_date || null,
          end_date: sessions.end_date || null,
          target_mode: sessions.target_mode,
          selected_session_ids:
            sessions.target_mode === "selected_sessions"
              ? sessions.selected_session_ids
              : [],
        },
      };
      const { data } = await api.put("/admin/automatic-discounts", body);
      applyPayload(data);
      toast.success("تم حفظ العروض التلقائية");
    } catch (e) {
      toast.error(extractApiErrorMessage(e, "تعذّر الحفظ"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <div
      className="mt-6 rounded-2xl border border-[#E5E1D8] bg-white p-5"
      data-testid="admin-auto-discounts-panel"
    >
      <h3 className="text-lg font-semibold text-[#0A4D3C] mb-1">
        العروض والخصومات التلقائية
      </h3>
      <p className="text-xs text-[#5E5C58] mb-4">
        خصم بدون كود يُطبَّق تلقائيًا خلال الفترة المحددة. لا يمكن الجمع بينه وبين
        كوبون. توقيت التقييم بتوقيت السعودية.
      </p>

      {/* BOOK PANEL */}
      <div
        className="rounded-xl border border-[#E5E1D8] p-4 mb-4"
        data-testid="admin-auto-discount-book"
      >
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-[#222222]">خصم الكتاب</h4>
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="w-4 h-4 accent-[#0A4D3C]"
              data-testid="auto-book-enabled"
              checked={book.enabled}
              onChange={(e) => setBook({ ...book, enabled: e.target.checked })}
            />
            تفعيل الخصم
          </label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="block">
            <span className="text-sm text-[#5E5C58]">نسبة الخصم %</span>
            <input
              type="number"
              min={1}
              max={99}
              step={0.1}
              value={book.percentage}
              onChange={(e) => setBook({ ...book, percentage: e.target.value })}
              className="mt-1 w-full rounded-xl border border-[#E5E1D8] bg-white px-3 py-2"
              data-testid="auto-book-percentage"
            />
          </label>
          <label className="block">
            <span className="text-sm text-[#5E5C58]">تاريخ البداية</span>
            <input
              type="date"
              value={book.start_date}
              onChange={(e) => setBook({ ...book, start_date: e.target.value })}
              className="mt-1 w-full rounded-xl border border-[#E5E1D8] bg-white px-3 py-2"
              data-testid="auto-book-start"
            />
          </label>
          <label className="block">
            <span className="text-sm text-[#5E5C58]">تاريخ النهاية</span>
            <input
              type="date"
              value={book.end_date}
              onChange={(e) => setBook({ ...book, end_date: e.target.value })}
              className="mt-1 w-full rounded-xl border border-[#E5E1D8] bg-white px-3 py-2"
              data-testid="auto-book-end"
            />
          </label>
        </div>
      </div>

      {/* SESSIONS PANEL */}
      <div
        className="rounded-xl border border-[#E5E1D8] p-4"
        data-testid="admin-auto-discount-sessions"
      >
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-[#222222]">خصم الجلسات</h4>
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="w-4 h-4 accent-[#0A4D3C]"
              data-testid="auto-sessions-enabled"
              checked={sessions.enabled}
              onChange={(e) =>
                setSessions({ ...sessions, enabled: e.target.checked })
              }
            />
            تفعيل الخصم
          </label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="block">
            <span className="text-sm text-[#5E5C58]">نسبة الخصم %</span>
            <input
              type="number"
              min={1}
              max={99}
              step={0.1}
              value={sessions.percentage}
              onChange={(e) =>
                setSessions({ ...sessions, percentage: e.target.value })
              }
              className="mt-1 w-full rounded-xl border border-[#E5E1D8] bg-white px-3 py-2"
              data-testid="auto-sessions-percentage"
            />
          </label>
          <label className="block">
            <span className="text-sm text-[#5E5C58]">تاريخ البداية</span>
            <input
              type="date"
              value={sessions.start_date}
              onChange={(e) =>
                setSessions({ ...sessions, start_date: e.target.value })
              }
              className="mt-1 w-full rounded-xl border border-[#E5E1D8] bg-white px-3 py-2"
              data-testid="auto-sessions-start"
            />
          </label>
          <label className="block">
            <span className="text-sm text-[#5E5C58]">تاريخ النهاية</span>
            <input
              type="date"
              value={sessions.end_date}
              onChange={(e) =>
                setSessions({ ...sessions, end_date: e.target.value })
              }
              className="mt-1 w-full rounded-xl border border-[#E5E1D8] bg-white px-3 py-2"
              data-testid="auto-sessions-end"
            />
          </label>
        </div>
        <div className="mt-3">
          <span className="text-sm text-[#5E5C58]">ينطبق على</span>
          <div className="mt-1 flex gap-3 items-center">
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="auto-sessions-target"
                value="all_sessions"
                checked={sessions.target_mode === "all_sessions"}
                onChange={() =>
                  setSessions({ ...sessions, target_mode: "all_sessions" })
                }
                data-testid="auto-sessions-target-all"
              />
              كل الجلسات
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="auto-sessions-target"
                value="selected_sessions"
                checked={sessions.target_mode === "selected_sessions"}
                onChange={() =>
                  setSessions({ ...sessions, target_mode: "selected_sessions" })
                }
                data-testid="auto-sessions-target-selected"
              />
              جلسات محددة
            </label>
          </div>
          {sessions.target_mode === "selected_sessions" && (
            <div
              className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2"
              data-testid="auto-sessions-choices"
            >
              {choices.map((s) => (
                <label
                  key={s.id}
                  className="inline-flex items-center gap-2 rounded-xl border border-[#E5E1D8] bg-[#FAF9F6] px-3 py-2 text-sm"
                >
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-[#0A4D3C]"
                    data-testid={`auto-sessions-choice-${s.id}`}
                    checked={sessions.selected_session_ids.includes(s.id)}
                    onChange={() => toggleChoice(s.id)}
                  />
                  <span className="flex-1">{s.title_ar}</span>
                  <span className="text-xs text-[#5E5C58]">{s.price} ر.س</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <Button
          onClick={save}
          disabled={saving}
          className="rounded-full bg-[#0A4D3C] hover:bg-[#063A2D] text-white"
          data-testid="auto-discounts-save"
        >
          {saving ? "جاري الحفظ..." : "حفظ"}
        </Button>
      </div>
    </div>
  );
};

export default AutoDiscountsPanel;
